import React from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Chip,
    Badge,
    TextField,
    InputAdornment,
    alpha,
} from "@mui/material";
import {
    Search as SearchIcon,
    Close as CloseIcon,
    ArrowBack,
    RestaurantMenu as RestaurantMenuIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Home as HomeIcon,
    Tune as TuneIcon,
    ShoppingCart,
    Stars,
} from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import { useTheme } from "@mui/material/styles";
import ImageLoader from "components/ImageLoader";
import GuestMenuHeader from "pages/GuestPortal/components/GuestMenuHeader";

const MenuHeaderBar = ({
    luxury = false,
    isPublicMode,
    guestMode,
    locationLabel,
    locationSubtitle,
    isRoom,
    companyData,
    onPrimaryNavigation,
    onDesktopBack,
    onNavigateRestaurants,
    onOpenCompanyInfo,
    onOpenRating,
    onOpenCart,
    cartItemCount,
    customerProfile,
    enteredMobile,
    searchQuery,
    onSearchQueryChange,
    onClearSearch,
    activeFilterCount,
    onOpenMobileFilter,
}) => {
    const theme = useTheme();

    if (luxury) {
        return (
            <GuestMenuHeader
                isPublicMode={isPublicMode}
                guestMode={guestMode}
                locationLabel={locationLabel}
                locationSubtitle={locationSubtitle}
                isRoom={isRoom}
                companyData={companyData}
                onPrimaryNavigation={onPrimaryNavigation}
                onDesktopBack={onDesktopBack}
                onOpenCompanyInfo={onOpenCompanyInfo}
                onOpenCart={onOpenCart}
                cartItemCount={cartItemCount}
                searchQuery={searchQuery}
                onSearchQueryChange={onSearchQueryChange}
                onClearSearch={onClearSearch}
                activeFilterCount={activeFilterCount}
                onOpenMobileFilter={onOpenMobileFilter}
            />
        );
    }
    return (
        <Paper
            elevation={0}
            sx={{
                zIndex: 1100,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                bgcolor: "transparent",
                backdropFilter: "none",
                boxShadow: "none",
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    background: "linear-gradient(140deg, #fffaf2 0%, #fff 55%, #f8fafc 100%)",
                    px: 1.5,
                    pt: 1.25,
                    pb: 1.75,
                    gap: 1.15,
                    position: "relative",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(circle at 86% 18%, rgba(245,158,11,0.12) 0%, transparent 40%)",
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <IconButton
                        onClick={onPrimaryNavigation}
                        size="small"
                        sx={{
                            position: "relative",
                            flexShrink: 0,
                            width: 36,
                            height: 36,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                            transition: "all 0.2s",
                        }}
                    >
                        {isPublicMode && !guestMode ? (
                            <HomeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        ) : (
                            <ArrowBack sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        )}
                    </IconButton>

                    <Box
                        onClick={onOpenCompanyInfo}
                        sx={{
                            position: "relative",
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                        }}
                    >
                        {companyData?.logo && (
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 38,
                                    height: 38,
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.12)",
                                }}
                            >
                                <ImageLoader
                                    imagePath={companyData.logo}
                                    width={38}
                                    height={38}
                                    alt={companyData.name}
                                    rounded={false}
                                />
                            </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "1rem",
                                    color: "#0f172a",
                                    lineHeight: 1.25,
                                    letterSpacing: 0.1,
                                    ...(guestMode
                                      ? { wordBreak: "break-word" }
                                      : {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }),
                                }}
                            >
                                {guestMode === "room-service"
                                    ? "Room dining"
                                    : companyData?.name || "Restaurant Menu"}
                            </Typography>
                            {locationLabel ? (
                                <Typography sx={{ fontSize: "0.68rem", color: "#b45309", fontWeight: 700, mt: 0.15, lineHeight: 1.3, wordBreak: "break-word" }}>
                                    {guestMode === "room-service" ? `Deliver to ${locationLabel}` : locationLabel}
                                </Typography>
                            ) : parseFloat(companyData?.averageRating) > 0 ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.15 }}>
                                    <StarIcon sx={{ fontSize: 11, color: "#fde68a" }} />
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#b45309", lineHeight: 1 }}>
                                        {parseFloat(companyData.averageRating).toFixed(1)}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.67rem", color: "#64748b", lineHeight: 1 }}>
                                        Â· {companyData.totalRatings} reviews
                                    </Typography>
                                </Box>
                            ) : companyData?.slogan ? (
                                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", lineHeight: 1, mt: 0.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {companyData.slogan}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>

                    <IconButton
                        size="small"
                        onClick={onOpenRating}
                        sx={{
                            position: "relative",
                            flexShrink: 0,
                            width: 36,
                            height: 36,
                            bgcolor: alpha("#fde68a", 0.3),
                            border: `1px solid ${alpha("#fde68a", 0.35)}`,
                            "&:hover": { bgcolor: alpha("#fde68a", 0.38) },
                            transition: "all 0.2s",
                        }}
                    >
                        <StarBorderIcon sx={{ fontSize: 18, color: "#b45309" }} />
                    </IconButton>

                    <IconButton
                        size="small"
                        onClick={onOpenCart}
                        sx={{
                            position: "relative",
                            flexShrink: 0,
                            width: 36,
                            height: 36,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                            transition: "all 0.2s",
                        }}
                    >
                        <Badge
                            badgeContent={cartItemCount}
                            sx={{
                                "& .MuiBadge-badge": {
                                    fontSize: "0.58rem",
                                    minWidth: 15,
                                    height: 15,
                                    bgcolor: "#fde68a",
                                    color: "#7c2d12",
                                    fontWeight: 800,
                                },
                            }}
                        >
                            <ShoppingCart sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        </Badge>
                    </IconButton>
                </Box>

                {isPublicMode && !guestMode && (
                    <Box
                        onClick={onNavigateRestaurants}
                        sx={{
                            alignSelf: "flex-start",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                        }}
                    >
                        <ArrowBack sx={{ fontSize: 13, color: theme.palette.primary.main }} />
                        <RestaurantMenuIcon sx={{ fontSize: 13, color: theme.palette.primary.main }} />
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1 }}>
                            All Restaurants
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                    {customerProfile && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.6, flexWrap: "wrap", width: "100%" }}>
                            <Chip
                                size="small"
                                icon={<PersonIcon sx={{ fontSize: "0.85rem !important" }} />}
                                label={customerProfile?.name || "Guest"}
                                sx={{
                                    height: 21,
                                    bgcolor: alpha("#6b7dcf", 0.1),
                                    color: "#4f5fa8",
                                    fontWeight: 700,
                                    border: `1px solid ${alpha("#6b7dcf", 0.24)}`,
                                    "& .MuiChip-label": { px: 0.8, fontSize: "0.66rem" },
                                }}
                            />
                            <Chip
                                size="small"
                                icon={<Stars sx={{ fontSize: "0.85rem !important" }} />}
                                label={`${parseInt(customerProfile?.Loyalty?.totalPoints || 0, 10)} pts`}
                                sx={{
                                    height: 21,
                                    bgcolor: alpha("#f59e0b", 0.12),
                                    color: "#b45309",
                                    fontWeight: 700,
                                    border: `1px solid ${alpha("#f59e0b", 0.28)}`,
                                    "& .MuiChip-label": { px: 0.8, fontSize: "0.66rem" },
                                }}
                            />
                            {!!(enteredMobile || customerProfile?.mobile) && (
                                <Chip
                                    size="small"
                                    label={enteredMobile || customerProfile?.mobile}
                                    sx={{
                                        height: 21,
                                        bgcolor: alpha("#0f172a", 0.05),
                                        color: "#475569",
                                        fontWeight: 700,
                                        border: `1px solid ${alpha("#0f172a", 0.12)}`,
                                        "& .MuiChip-label": { px: 0.8, fontSize: "0.66rem" },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: "#ffffff",
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                                px: 1.5,
                                height: 42,
                                transition: "all 0.2s",
                                "&:focus-within": {
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                                },
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 17, color: "#64748b", flexShrink: 0 }} />
                            <Box
                                component="input"
                                placeholder="Search dishes..."
                                value={searchQuery}
                                onChange={(e) => onSearchQueryChange(e.target.value)}
                                sx={{
                                    flex: 1,
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    color: "#0f172a",
                                    fontSize: "0.88rem",
                                    fontFamily: theme.typography.fontFamily,
                                    "::placeholder": { color: "#94a3b8" },
                                }}
                            />
                            {searchQuery && (
                                <IconButton
                                    size="small"
                                    onClick={onClearSearch}
                                    sx={{ p: 0.25, color: "#64748b", "&:hover": { color: "#0f172a" } }}
                                >
                                    <CloseIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            )}
                        </Box>

                        <Box
                            component="button"
                            onClick={onOpenMobileFilter}
                            sx={{
                                flexShrink: 0,
                                width: 42,
                                height: 42,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 2,
                                border: `1.5px solid ${activeFilterCount > 0 ? "#fde68a" : alpha("#fff", 0.3)}`,
                                bgcolor: activeFilterCount > 0 ? alpha("#fde68a", 0.25) : "#ffffff",
                                cursor: "pointer",
                                position: "relative",
                                transition: "all 0.2s",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                            }}
                        >
                            <TuneIcon sx={{ fontSize: 19, color: activeFilterCount > 0 ? "#b45309" : theme.palette.primary.main }} />
                            {activeFilterCount > 0 && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -4,
                                        right: -4,
                                        width: 16,
                                        height: 16,
                                        borderRadius: "50%",
                                        bgcolor: "#fde68a",
                                        color: theme.palette.primary.dark,
                                        fontSize: "0.6rem",
                                        fontWeight: 800,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontFamily: theme.typography.fontFamily,
                                    }}
                                >
                                    {activeFilterCount}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                    px: 4,
                    borderRadius: 0,
                    border: "none",
                    background: "transparent",
                    boxShadow: "none",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    {(!isPublicMode || guestMode) && (
                        <IconButton
                            onClick={onDesktopBack}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                    )}
                    {companyData?.logo && (
                        <Box
                            onClick={onOpenCompanyInfo}
                            sx={{ cursor: "pointer", "&:hover": { opacity: 0.85 }, transition: "opacity 0.2s" }}
                        >
                            <ImageLoader
                                imagePath={companyData.logo}
                                width={44}
                                height={44}
                                alt={companyData.name}
                                rounded={true}
                                borderRadius={2}
                            />
                        </Box>
                    )}
                    <Box>
                        <Typography
                            variant="h6"
                            onClick={onOpenCompanyInfo}
                            sx={{
                                fontWeight: 800,
                                color: "#0f172a",
                                fontSize: "1.3rem",
                                cursor: "pointer",
                                "&:hover": { opacity: 0.8 },
                            }}
                        >
                            {companyData?.name || "Restaurant Menu"}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="inherit"
                            onClick={onOpenCompanyInfo}
                            sx={{ cursor: "pointer", color: "#64748b", "&:hover": { opacity: 0.85 } }}
                        >
                            {companyData?.slogan || "Order your favorite dishes"}
                        </Typography>
                        {parseFloat(companyData?.averageRating) > 0 && (
                            <Box
                                onClick={onOpenCompanyInfo}
                                sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.4, cursor: "pointer", "&:hover": { opacity: 0.75 } }}
                            >
                                <StarIcon sx={{ fontSize: 15, color: "#f59e0b" }} />
                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#f59e0b", lineHeight: 1 }}>
                                    {parseFloat(companyData.averageRating).toFixed(1)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#64748b", lineHeight: 1 }}>
                                    ({companyData.totalRatings})
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box sx={{ flex: { md: 2, lg: 1.5 }, maxWidth: 500, display: "flex", flexDirection: "column", gap: 0.8 }}>
                    {customerProfile && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, flexWrap: "wrap", width: "100%" }}>
                            <Chip
                                size="small"
                                icon={<PersonIcon sx={{ fontSize: "0.95rem !important" }} />}
                                label={customerProfile?.name || "Guest"}
                                sx={{
                                    height: 24,
                                    bgcolor: alpha("#6b7dcf", 0.1),
                                    color: "#4f5fa8",
                                    fontWeight: 700,
                                    border: `1px solid ${alpha("#6b7dcf", 0.24)}`,
                                    "& .MuiChip-label": { px: 0.85, fontSize: "0.72rem" },
                                }}
                            />
                            <Chip
                                size="small"
                                icon={<Stars sx={{ fontSize: "0.95rem !important" }} />}
                                label={`Loyalty: ${parseInt(customerProfile?.Loyalty?.totalPoints || 0, 10)} pts`}
                                sx={{
                                    height: 24,
                                    bgcolor: alpha("#f59e0b", 0.12),
                                    color: "#b45309",
                                    fontWeight: 700,
                                    border: `1px solid ${alpha("#f59e0b", 0.28)}`,
                                    "& .MuiChip-label": { px: 0.85, fontSize: "0.72rem" },
                                }}
                            />
                            {!!(enteredMobile || customerProfile?.mobile) && (
                                <Chip
                                    size="small"
                                    label={enteredMobile || customerProfile?.mobile}
                                    sx={{
                                        height: 24,
                                        bgcolor: alpha("#0f172a", 0.05),
                                        color: "#475569",
                                        fontWeight: 700,
                                        border: `1px solid ${alpha("#0f172a", 0.12)}`,
                                        "& .MuiChip-label": { px: 0.85, fontSize: "0.72rem" },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search dishes..."
                        value={searchQuery}
                        onChange={(e) => onSearchQueryChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={onClearSearch}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                height: 44,
                                borderRadius: 2,
                                bgcolor: "#ffffff",
                                border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                                "&:hover": { bgcolor: alpha(theme.palette.background.default, 0.72) },
                                "&.Mui-focused": {
                                    bgcolor: "#ffffff",
                                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    color: "#94a3b8",
                                },
                                "& .MuiSvgIcon-root": {
                                    color: "#64748b",
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

export default MenuHeaderBar;
